/* eslint-disable no-nested-ternary */
import { CloseOutlined, ExclamationCircleFilled, PlusCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { createForm } from '@formily/core';
import { Button, Drawer, Form, Input, message, Select, Space, Tooltip, Card, FormInstance, Modal } from 'antd';
import classnames from 'classnames';
import { get, isEmpty } from 'lodash-es';
import React, { FC, useEffect, useRef, useState, useContext } from 'react';
import IconClose from '@/assets/images/icons/icon-close.react.svg?react';
import { BusiCode } from '@/infrastructure/axios/code';
import { hintText } from '@/presentation/components/hint-text/hintText';
import { PlatformContext } from '@/presentation/pages/setting/app-deliver-config';
import {
  ConfigTabType,
  FormatConfig,
  insertConfig,
  isConfigManage,
  ModalFormData,
  updateConfig,
} from '@/presentation/pages/setting/app-deliver-config/api';
import { ComplianceEditor, transferToConfig } from '@/presentation/pages/setting/app-deliver-config/compliance-editor';
import {
  ConfigurationContent,
} from '@/presentation/pages/setting/app-deliver-config/components/configuration-content';
import { PLATFORM_OPTIONS } from '@/presentation/pages/setting/app-deliver-config/config-header';
import { ConfirmModal } from '@/presentation/pages/setting/app-deliver-config/config-modal/confirm';
import { DiffModal } from '@/presentation/pages/setting/app-deliver-config/config-modal/edit/diff-modal';
import { SampleRatio } from '@/presentation/pages/setting/app-deliver-config/type';
import { transform2Config, transform2Form } from '@/presentation/pages/setting/app-deliver-config/utils';
import { useStores } from '@/presentation/store';
import style from './index.module.scss';

const { TextArea } = Input;

export const APP_VERSION_MODE_OPTIONS: SelectOptions[] = [
  {
    label: '不限',
    value: '',
  },
  {
    label: '开发版',
    value: 'Debug',
  },
  {
    label: '灰度版',
    value: 'Gray',
  },
  {
    label: '正式版',
    value: 'Release',
  },
];

// 版本类型支持的关系规则
export const VERSION_MATCH_OPTIONS = [
  {
    label: '是',
    value: 'OR',
  },
  {
    label: '大于',
    value: 'GT',
  },
  {
    label: '大于等于',
    value: 'GE',
  },
  {
    label: '小于',
    value: 'LT',
  },
  {
    label: '小于等于',
    value: 'LE',
  },
];

export const operatorOptions = {
  OR: '是',
  GT: '大于',
  GE: '大于等于',
  LT: '小于',
  LE: '小于等于',
};

interface SelectOptions {
  label: string;
  value: string;
}

interface ConfigInputProps {
  size: 'large' | 'small';
  name: string;
  label: React.ReactNode | string;
  isRequired?: boolean;
  rules?: object[];
  isIcon?: boolean;
  tooltip?: React.ReactNode;
  placeholder?: string;
}

export interface IConfig {
  appVersion: string[];
  system: string[];
  appVersionMode: string[];
  name: string;
  memo: string;
  sdkVersion: string[];
  appVersionOperator: string,
  sdkVersionOperator: string,
  osVersionOperator: string,
  model: string[];
  uin: string[];
  priority: number | string;
  deviceId: string[];
  productId: string | string[];
}

// 输入框
const ConfigInput: FC<ConfigInputProps> = (props) => {
  const { size, name, label, isRequired, rules, isIcon, placeholder, tooltip } = props;
  let inputWidth = '100%';
  if (size === 'small') {
    if (isIcon) {
      inputWidth = '224px';
    } else {
      inputWidth = '188px';
    }
  }
  return (
    <Form.Item
      name={name}
      label={label}
      required={isRequired}
      rules={rules}
    >
      <Input
        style={{
          width: inputWidth,
          marginBottom: 0,
        }}
        className={style.configInput}
        placeholder={placeholder}
        addonAfter={isIcon ? <Tooltip title={tooltip}>
          <QuestionCircleOutlined style={{ color: '#A3A3A9', fontSize: '14px' }}/>
        </Tooltip> : null}
      />
    </Form.Item>
  );
};

interface ConfigItemType {
  configLabel: string;
  configItem: string;
  configValue?: string[];
  operator?: string;
  options?: SelectOptions[];
  visible?: ConfigTabType;
  showDelete?: boolean
}

const getDefaultConfigList = (tab: ConfigTabType) => [
  { configLabel: '构建类型', configItem: 'appVersionMode', disable: tab === ConfigTabType.COMPLIANCE, options: APP_VERSION_MODE_OPTIONS, configValue: [''] },
  { configLabel: 'APP版本', configItem: 'appVersion', operatorOptions: VERSION_MATCH_OPTIONS, operator: VERSION_MATCH_OPTIONS[0].value },
  { configLabel: 'SDK版本', configItem: 'sdkVersion', operatorOptions: VERSION_MATCH_OPTIONS, operator: VERSION_MATCH_OPTIONS[0].value },
  { configLabel: '系统版本', configItem: 'system', operatorOptions: VERSION_MATCH_OPTIONS, operator: VERSION_MATCH_OPTIONS[0].value },
  { configLabel: '机型', configItem: 'model' },
  { configLabel: '用户白名单', configItem: 'uin' },
  { configLabel: '设备白名单', configItem: 'deviceId' },
  { configLabel: 'App ID', configItem: 'productId', disable: tab === ConfigTabType.BUSINESS || tab === ConfigTabType.COMPLIANCE },
].filter(item => !item.disable);

const renderConfigItem = (
  configList: ConfigItemType[],
  setConfigList: React.Dispatch<React.SetStateAction<ConfigItemType[]>>,
  tab: ConfigTabType,
  form: FormInstance,
) => {
  const defaultConfigList = getDefaultConfigList(tab);
  const addConfig = () => {
    const newConfig = defaultConfigList.filter(item => !configList
      .some(config => config.configItem === item.configItem));
    setConfigList([...configList, newConfig[0]]);
  };

  const deleteConfig = (deleteIndex: number) => {
    const newConfig = configList.filter((_: unknown, configIndex: number) => configIndex !== deleteIndex);
    setConfigList(newConfig);
  };

  const getAppVersionModeValue = (values: string[]): string[] => {
    if (values.length > 1 && values[0] === APP_VERSION_MODE_OPTIONS[0].value) {
      const filterAll = values.filter(item => item !== APP_VERSION_MODE_OPTIONS[0].value);
      form.setFieldsValue({ appVersionMode: filterAll });
      return filterAll;
    }
    if (values.slice(-1)?.[0] === APP_VERSION_MODE_OPTIONS[0].value && values.length > 1) {
      const selectAll = values.filter(item => item === APP_VERSION_MODE_OPTIONS[0].value);
      form.setFieldsValue({ appVersionMode: selectAll });
      return selectAll;
    }
    return values;
  };

  const onChangeConfig = (changeValue: string, index: number) => {
    if (configList.find(item => item.configItem === changeValue)) {
      setConfigList(configList);
      message.warning('配置项重复，请重新选择');
      return;
    }
    const changeConfig = configList.map((config, configIndex) => {
      if (configIndex === index) {
        return defaultConfigList.find(item => item.configItem === changeValue)!;
      }
      return config;
    });
    setConfigList(changeConfig);
  };

  // 同步更新form表单的值
  const updateFormConfig = (configItem: string, changeValue: string[] = [], operator: string | undefined) => {
    let newValue = changeValue;
    if (configItem === 'appVersion') {
      if (operator !== 'OR' && changeValue?.length > 1) newValue = newValue?.slice(-1);
      form.setFieldsValue({
        appVersion: newValue,
      });
    } else if (configItem === 'sdkVersion') {
      if (operator !== 'OR' && newValue?.length > 1) newValue = newValue?.slice(-1);
      form.setFieldsValue({
        sdkVersion: newValue,
      });
    } else if (configItem === 'system') {
      if (operator !== 'OR' && newValue?.length > 1) newValue = newValue?.slice(-1);
      form.setFieldsValue({
        system: newValue,
      });
    }
    return newValue;
  };

  const onChangeConfigValue = (changeValue: string[], index: number, configItem: string) => {
    const changeConfigValue = configList.map((config, configIndex) => {
      if (configIndex === index) {
        const newValue = updateFormConfig(configItem, changeValue, config.operator);
        return ({
          ...config,
          configValue: configItem === 'appVersionMode' ? getAppVersionModeValue(changeValue) : newValue,
        });
      }
      return config;
    });
    setConfigList(changeConfigValue);
  };

  // 切换匹配规则
  const onChangeconfigOperator = (changeValue: string, index: number) => {
    const changeconfigOperator = configList.map((config, configIndex) => {
      if (configIndex === index) {
        if (config.configItem === 'appVersion') {
          form.setFieldsValue({ appVersionOperator: changeValue });
        } else if (config.configItem === 'sdkVersion') {
          form.setFieldsValue({ sdkVersionOperator: changeValue });
        } else if (config.configItem === 'system') {
          form.setFieldsValue({ osVersionOperator: changeValue });
        }
        const newValue = updateFormConfig(config.configItem, config.configValue, changeValue) || [];
        return ({
          ...config,
          operator: changeValue,
          configValue: newValue,
        });
      }
      return config;
    });
    setConfigList(changeconfigOperator);
  };

  // 确定是哪一个匹配规则
  const getConfigOperator = (configItem: string) => {
    if (configItem === 'appVersion') {
      return 'appVersionOperator';
    }
    if (configItem === 'sdkVersion') {
      return 'sdkVersionOperator';
    }
    if (configItem === 'system') {
      return 'osVersionOperator';
    }
    return '';
  };

  const onMouse = (index: number, showDelete: boolean) => {
    const changeConfig = configList.map((config, configIndex) => {
      if (configIndex === index) {
        return ({
          ...config,
          showDelete,
        });
      }
      return config;
    });
    setConfigList(changeConfig);
  };

  return <Card bodyStyle={{ background: '#F6F8FB' }}>
    <div style={{ marginBottom: '15px' }}>以下条件为全且关系</div>
    {configList.map((item, index) => <div
      style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '12px',
      }}
      onMouseOver={() => onMouse(index, true)}
      onMouseLeave={() => onMouse(index, false)}
    >
      <Select
        options={defaultConfigList.map(item => ({ label: item.configLabel, value: item.configItem }))}
        style={{ width: '120px', marginRight: '10px' }}
        defaultValue={item.configItem}
        value={item.configItem}
        onChange={(changeValue: string) => onChangeConfig(changeValue, index)}
      />
      {item.configItem !== 'appVersionMode'
        ? <Form.Item name={ getConfigOperator(item.configItem) } noStyle>
          <Select
            options={
              defaultConfigList.find(config => config.configItem === item.configItem)?.operatorOptions
            || [VERSION_MATCH_OPTIONS[0]]
            }
            key={item.operator}
            style={{ width: '106px', marginRight: '10px' }}
            defaultValue={ VERSION_MATCH_OPTIONS[0].value }
            value={ item.operator }
            onChange={(configOperatorValue: string) => onChangeconfigOperator(configOperatorValue, index)}
          />
        </Form.Item> : null}
      <Form.Item name={item.configItem} style={{ marginBottom: '0', flex: 1 }}>
        <Select
          mode="tags"
          open={(item.configItem !== defaultConfigList[0].configItem) ? false : undefined}
          placeholder={item.configItem === defaultConfigList[0].configItem
            ? `请选择${item.configLabel}`
            : ((item.operator !== 'OR' && item.operator) ? `输入${item.configLabel}，请输入APP版本，而且只能输入一个`
              : `请输入${item.configLabel}，批量输入以英文竖线（|）分割`
            )}
          allowClear
          autoClearSearchValue
          tokenSeparators={['|', '｜']}
          options={item.options}
          onChange={(changeValue: string[]) => onChangeConfigValue(changeValue, index, item.configItem)}
          defaultValue={item.configValue}
          value={item.configValue}
        />
      </Form.Item>
      <div className={style.conditionCloseIcon}>
        {(configList.length > 1 && item.showDelete) && <CloseOutlined onClick={() => deleteConfig(index)}/>}
      </div>

    </div>)}
    {configList.length !== defaultConfigList.length && <div className={style.addConfigBtn} onClick={addConfig}>
      <PlusCircleOutlined style={{ marginRight: '8px', fontSize: 16, verticalAlign: 'text-top' }}/>
      添加
    </div>}
  </Card>;
};

interface ConfigTextAreaProps {
  name: string;
  label: string | React.ReactNode;
  autoSize?: boolean | object;
  required?: boolean;
  rules?: object[];
  placeholder?: string;
}

// 文本框
const ConfigTextArea: FC<ConfigTextAreaProps> = (props) => {
  const { name, label, autoSize, required, rules, placeholder } = props;
  return (
    <Form.Item
      name={name}
      label={label}
      required={required}
      rules={rules}
    >
      <TextArea
        autoSize={autoSize}
        placeholder={placeholder}
        showCount
        maxLength={140}
        style={{ width: '100%' }}
      />
    </Form.Item>
  );
};

export enum OperationType {
  ADD,
  UPDATE,
  VIEW,
  COPY,
}

export const TITLE_MAP = {
  [OperationType.ADD]: '新建配置',
  [OperationType.UPDATE]: '编辑配置',
  [OperationType.COPY]: '复制配置',
};

export interface EditConfigModalProps {
  visible: boolean;
  type: OperationType;
  tab: ConfigTabType;
  onChange: () => void;
  onCancel: () => void;
  values?: FormatConfig;
  defaultConfig?: string;
}

// 新建/编辑配置弹窗
export const EditConfigModal: FC<EditConfigModalProps> = (props) => {
  const { visible, type, tab, values, onChange, onCancel, defaultConfig } = props;
  const defaultConfigList = getDefaultConfigList(tab);
  const defaultContent = values?.content || defaultConfig || '{}';
  const [updateConfirmModal, setUpdateConfirmModal] = useState(false);
  const [isDiff, setIsDiff] = useState(false);
  const [lockWarningVisible, setLockWarningVisible] = useState(false);
  const [form] = Form.useForm();
  const [formilyForm, setFormilyForm] = useState(createForm());
  const [configList, setConfigList] = useState<ConfigItemType[]>([defaultConfigList[0]]);
  const [metricContent, setMetricContent] = useState<string>(defaultContent);
  const editorRef = useRef<any>();
  const [contentForm] = Form.useForm();
  const { addPlatform, setAddPlatform } = useContext(PlatformContext);
  const {
    configs: operation = {},
    androidFullConfig = {},
    iosFullConfig = {},
    flutterFullConfig = {},
    windowsFullConfig = {},
    harmonyOSFullConfig = {},
    macFullConfig = {},
    appleTVFullConfig = {},
  } = useStores().ConfigStore;
  const { configMode = {} } = operation;
  const fullConfigMode = {
    ...configMode.android,
    ...configMode.ios,
    ...configMode.flutter,
  };

  const isAdd = type === OperationType.ADD;
  const isUpdate = type === OperationType.UPDATE;
  const isCopy = type === OperationType.COPY;

  // 获取原始配置的 sampleRatio
  const prevConfigData = isUpdate && defaultContent
    ? transform2Form(
      defaultContent,
      androidFullConfig,
      iosFullConfig,
      flutterFullConfig,
      windowsFullConfig,
      harmonyOSFullConfig,
      macFullConfig,
      appleTVFullConfig,
      fullConfigMode,
    )
    : undefined;
  const prevSampleRatio = prevConfigData?.sampleRatio;

  useEffect(() => {
    form.setFieldsValue(values);
    const configKeys = values && Object.keys(values as {});
    const configList = defaultConfigList.filter(item => configKeys?.includes(item.configItem))
      .map((item) => {
        let operator = '';
        if (item.configItem === 'appVersion') {
          operator = values?.appVersionOperator || 'OR';
          form.setFieldsValue({ appVersionOperator: operator });
        } else if (item.configItem === 'system') {
          operator = values?.osVersionOperator || 'OR';
          form.setFieldsValue({ osVersionOperator: operator });
        } else if (item.configItem === 'sdkVersion') {
          operator = values?.sdkVersionOperator || 'OR';
          form.setFieldsValue({ sdkVersionOperator: operator });
        }
        return {
          operator,
          ...item,
          configValue: values?.[item.configItem as keyof FormatConfig],
        };
      })
      .filter(item => !isEmpty(item.configValue) || item.configItem === 'appVersionMode');
    (isUpdate || isCopy) && setConfigList(configList as ConfigItemType[]);
  }, [values]);

  useEffect(() => {
    if (visible) {
      setFormilyForm(createForm());
      isAdd && setConfigList([defaultConfigList[0]]);
    }
  }, [visible]);

  useEffect(() => {
    setMetricContent(values?.content ?? metricContent);
  }, [values?.content]);

  const messageKey = isAdd ? 'addConfig' : 'updateConfig';

  // 点击确认回调事件
  const onOk = async () => {
    try {
      await form.validateFields();
      await contentForm.validateFields();
      await formilyForm.validate();
    } catch (error) {
      return; // 表单验证不通过
    }
    if (!(editorRef.current?.valid || formilyForm.valid)) {
      return;
    }
    const configItem: Record<string, string[] | undefined> = {};
    configList.forEach((item: ConfigItemType) => {
      configItem[item.configItem] = item.configValue;
    });
    let formValues: ModalFormData = form.getFieldsValue();
    formValues = {
      ...formValues,
      ...configItem,
      content: tab === ConfigTabType.COMPLIANCE
        ? JSON.stringify(transferToConfig(formilyForm.values)) : transform2Config(metricContent, addPlatform),
      platform: isAdd ? addPlatform : values?.platform,
    };

    // 检查：如果原始配置的全局采样率为0（已打开"关闭所有监控项"），且未调整为1，则不允许提交
    if (isUpdate && prevSampleRatio === SampleRatio.ON) {
      try {
        const newConfigData = JSON.parse(formValues.content || '{}');
        const newSampleRatio = newConfigData?.sampleRatio;

        // 如果 sampleRatio 没有被修改为 OFF，则弹出警告
        if (newSampleRatio !== SampleRatio.OFF) {
          setLockWarningVisible(true);
          return;
        }
      } catch (error) {
        // 解析失败，忽略检查，继续提交
      }
    }

    let reqData;
    if (isAdd || isCopy) {
      message.loading({ content: '添加配置中...', key: messageKey });
      reqData = await insertConfig(tab, formValues);
    } else {
      if (values?.enabled === 1 && tab === ConfigTabType.COMPLIANCE) {
        setUpdateConfirmModal(true);
        return;
      }
      if (tab === ConfigTabType.BUSINESS || isConfigManage(tab)) {
        setIsDiff(true);
        return;
      }
      message.loading({ content: '更新配置中...', key: messageKey });
      reqData = await updateConfig(
        tab,
        formValues,
        values?.createUser ?? '',
        values?.createTime ?? 0,
        values?.enabled ?? 0,
        values?.id ?? 0,
      );
    }
    // 判断返回情况
    if (reqData.baseRsp?.code === BusiCode.Ok) {
      const content = (isAdd || isCopy) ? '新增配置成功' : '更新配置成功';
      message.success({ content, key: messageKey, duration: 2 });
      onChange?.();
      form.resetFields();
      contentForm.resetFields();
    }
  };

  const onUpdateConfirmOk = async () => {
    let formValues: ModalFormData = form.getFieldsValue();
    const configList: Record<string, string[]> = {};
    defaultConfigList.map(item => item.configItem).forEach((item: string) => {
      configList[item] = [];
    });

    formValues = {
      ...configList,
      ...formValues,
      platform: isAdd ? addPlatform : values?.platform,
    };
    formValues.content = tab === ConfigTabType.COMPLIANCE
      ? JSON.stringify(transferToConfig(formilyForm.values)) : transform2Config(metricContent, addPlatform);
    message.loading({ content: '更新配置中...', key: messageKey });
    const reqData = await updateConfig(
      tab,
      formValues,
      values?.createUser ?? '',
      values?.createTime ?? 0,
      values?.enabled ?? 0,
      values?.id ?? 0,
    );
    if (reqData.baseRsp?.code === BusiCode.Ok) {
      message.success({ content: '更新配置成功', key: messageKey, duration: 2 });
      onChange?.();
      setUpdateConfirmModal(false);
      setIsDiff(false);
      form.resetFields();
    }
  };

  const onModalCancel = () => {
    if (isAdd) {
      form.resetFields();
    }
    onCancel();
  };

  const onChangeContent = (content: string) => {
    setMetricContent(content);
  };

  const initEditorContent = values?.content ?? defaultConfig;

  return (
    <Drawer
      destroyOnClose
      width={tab !== ConfigTabType.COMPLIANCE ? 965 : 750}
      maskClosable={false}
      visible={visible}
      title={get(TITLE_MAP, type, TITLE_MAP[OperationType.UPDATE])}
      className={style.modal}
      onClose={onModalCancel}
      footer={
        <Space className={style.footerSpace}>
          <Button onClick={onOk} type="primary">提交</Button>
          <Button onClick={onModalCancel}>取消</Button>
        </Space>
      }
    >
      <Form
        scrollToFirstError
        form={form}
        layout={'vertical'}
        labelAlign='right'
        style={{ height: '451px' }}
        initialValues={{
          appVersionOption: 'include',
          sdkVersionOption: 'include',
          systemOption: 'include',
        }}
        className={style.form}
      >
        {/* 任务名称 */}
        <ConfigInput
          size={'large'}
          name={'name'}
          label={'任务名称'}
          isRequired
          rules={[
            {
              required: true,
              message: '任务名称必填!',
            },
            {
              validator: (_rule: any, val: string, callback: (arg0?: string) => void) => {
                if (val && !/^[\u4e00-\u9fa5a-zA-Z0-9.,\\s@_\\\-()#:]+$/.test(val)) {
                  callback('任务名称包含非法字符');
                } else if (val.length > 30) {
                  callback('任务名称长度不能超过30个字符！');
                }
                callback();
              },
            },
          ]}
          placeholder={'请输入仅包含中文字符、英文字母、数字以及特殊字符（.、,、:、/、(、)、@、#、_、-）的字符串。'}
        />
        {isConfigManage(tab) && isAdd ? <Form.Item
          label={'平台'}
          required
        >
          <Select
            options={PLATFORM_OPTIONS}
            value={addPlatform}
            onChange={val => setAddPlatform?.(val)}
          />
        </Form.Item> : null}
        <Form.Item
          name={'configItems'}
          label={hintText('适用条件', '设置“适用条件”后，只有满足这些条件的用户才有机会拉取该配置任务。如果有多个配置任务同时满足用户的条件，系统会优先下发优先级最高的配置任务。')}
        >
          {renderConfigItem(configList, setConfigList, tab, form)}
        </Form.Item>
        {/* 优先级 */}
        <ConfigInput
          size={'large'}
          name={'priority'}
          label={hintText('优先级', '描述任务的优先级，当多个配置任务的条件被满足时，选择优先级最高\n'
            + '的任务下发给用户。数值越大，优先级越高。')}
          isRequired
          rules={[
            {
              required: true,
              message: '优先级必填!',
            },
            {
              validator: (_rule: any, val: number, callback: (arg0?: string) => void) => {
                if (val < 0) {
                  callback('优先级不能小于0！');
                }
                if (val > 99999) {
                  callback('优先级不能大于99999！');
                }
                callback();
              },
            },
          ]}
          placeholder={'输入1-99999'}
        />
        {/* 备注 */}
        <ConfigTextArea
          name={'memo'}
          label={hintText('备注', '描述任务的目的。')}
          placeholder={'请输入备注'}
        />
        {/* JSON配置 */}
        <Form.Item
          label={'配置内容'}
          className={classnames({
            [style.metricsConfig]: tab === ConfigTabType.BUSINESS,
          })}
          name={'content'}
          required
        >
          <div style={{ marginBottom: '14px' }}>
            {tab !== ConfigTabType.COMPLIANCE
              ? <ConfigurationContent
                  form={contentForm}
                  content={defaultContent}
                  onChange={onChangeContent}
                  tab={tab}
                  type={type}
                  fullContent={defaultConfig!}
                />
              : <ComplianceEditor
                  form={formilyForm}
                  initContent={JSON.parse(initEditorContent || '{}')}
                />}
          </div>
        </Form.Item>
        <ConfirmModal
          visible={updateConfirmModal}
          confirmTitle={'确认替换当前配置？'}
          confirmPrompt={'替换后，新配置内容会立即生效。请确保配置内容正常后再进行替换。'}
          icon={<ExclamationCircleFilled style={{ fontSize: '21px', color: '#F28B24' }}/>}
          onCancel={() => setUpdateConfirmModal(false)}
          onOk={onUpdateConfirmOk}
        />
        <Modal
          title={
            <div style={{ fontWeight: 600 }}>无法修改配置任务</div>
          }
          visible={lockWarningVisible}
          onCancel={() => setLockWarningVisible(false)}
          footer={
            <Button type="primary" onClick={() => setLockWarningVisible(false)}>
              确定
            </Button>
          }
          closeIcon={<IconClose/>}
          destroyOnClose={true}
        >
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 12 }}>
              由于当前配置任务已打开<span style={{ fontWeight: 500 }}>「关闭所有监控项」</span>开关，所有监控参数已被锁定，无法修改。
            </div>
            <div>
              <div style={{ marginBottom: 8, fontWeight: 600 }}>如何修改？</div>
              <div>
                请先关闭页面上方的<span style={{ fontWeight: 500 }}>「关闭所有监控项」</span>开关，保存更改后，即可解锁并调整具体的配置参数。
              </div>
            </div>
          </div>
        </Modal>
        <DiffModal
          prevForm={getForm(values as FormatConfig)}
          nextForm={getForm(form.getFieldsValue())}
          prevCode={defaultContent}
          nextCode={metricContent}
          visible={isDiff}
          onSubmit={onUpdateConfirmOk}
          onHide={() => setIsDiff(false)}
          type={'update'}
          tab={tab}
        />
      </Form>
    </Drawer>
  );
};

export const getForm = (form: FormatConfig): IConfig => ({
  name: form?.name ?? '',
  appVersion: form?.appVersion ?? [],
  sdkVersion: form?.sdkVersion ?? [],
  system: form?.system ?? [],
  model: form?.model ?? [],
  uin: form?.uin ?? [],
  deviceId: form?.deviceId ?? [],
  priority: form?.priority ?? '',
  memo: form?.memo ?? '',
  appVersionMode: form?.appVersionMode ?? [],
  appVersionOperator: form?.appVersionOperator ?? '',
  sdkVersionOperator: form?.sdkVersionOperator ?? '',
  osVersionOperator: form?.osVersionOperator ?? '',
  productId: form?.productId ?? [],
});
